import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
  Select,
  SimpleGrid,
  useBreakpointValue,
} from "@chakra-ui/react";
import { MdAdd, MdVisibility, MdRefresh } from "react-icons/md";
import { useTranslation } from "react-i18next";
import CreateTicket from "./CreateTicket";
import TicketDetail from "./TicketDetail";

const TicketsList = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Modal controls
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  
  // State
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [permissions, setPermissions] = useState({
    canList: true,
    canEdit: true,
    canInsert: true,
    canDelete: true,
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [limit] = useState(10); // Items per page
  
  const pageID = 22;
  const token = sessionStorage.getItem('accessToken'); // Adjust as needed

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) {
        toast({
          title: t("No permissions found"),
          description: t("You don't have the required access."),
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
        return;
      }

      // Use a tickets-specific page ID (you may need to adjust this)
      const permission = role.permissions.find((p) => p.page_id === pageID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canInsert: permission.insert_access || false,
          canDelete: permission.delete_access || false,
        });
      }
    } catch (error) {
      toast({
        title: t("Error loading permissions"),
        description: error.message || t("Something went wrong while fetching permissions."),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast, t]);

  // Fetch tickets
  const fetchTickets = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tickets?page_id=${pageID}&page=${page}&limit=${limit}`,{
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'privilege': 'read_all'
        },
      });
      const data = await response.json();

      if (data.status === "success") {
        setTickets(data.data);
        setFilteredTickets(data.data);
        
        // Handle pagination metadata
        if (data.pagination) {
          setCurrentPage(data.pagination.current_page || page);
          setTotalPages(data.pagination.last_page || 1);
          setTotalTickets(data.pagination.total || data.data.length);
        } else {
          // Fallback if no pagination data
          setCurrentPage(page);
          setTotalPages(1);
          setTotalTickets(data.data.length);
        }
      } else {
        throw new Error(data.message || "Failed to fetch tickets");
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast({
        title: t("Error fetching tickets"),
        description: error.message || t("Failed to load tickets"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [pageID, limit, token, toast, t]);

  // Initial fetch
  useEffect(() => {
    if (permissions.canList) {
      fetchTickets(currentPage);
    }
  }, [permissions.canList, fetchTickets, currentPage]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchTickets(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    
    const filtered = tickets.filter(ticket => 
      ticket.subject.toLowerCase().includes(query) ||
      ticket.id.toString().includes(query)
    );
    
    setFilteredTickets(filtered);
    
    // Reset to first page when searching
    if (query !== searchQuery) {
      setCurrentPage(1);
    }
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    
    if (status === "") {
      setFilteredTickets(tickets);
    } else {
      const filtered = tickets.filter(ticket => ticket.status === status);
      setFilteredTickets(filtered);
    }
    
    // Reset to first page when filtering
    setCurrentPage(1);
  };

  // Handle ticket view
  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    onDetailOpen();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "yellow";
      case "closed":
        return "gray";
      default:
        return "gray";
    }
  };

  // Handle ticket created
  const handleTicketCreated = () => {
    onCreateClose();
    fetchTickets(currentPage);
    toast({
      title: t("Success"),
      description: t("Ticket created successfully"),
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  // Handle ticket updated
  const handleTicketUpdated = () => {
    onDetailClose();
    fetchTickets(currentPage);
    toast({
      title: t("Success"),
      description: t("Ticket updated successfully"),
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  // Mobile card view
  const renderMobileView = () => (
    <SimpleGrid columns={1} spacing={4} p={4}>
      {filteredTickets.map((ticket) => (
        <Card key={ticket.id} p={4} shadow="md" borderRadius="md">
          <Flex justify="space-between" align="start" mb={2}>
            <Text fontWeight="bold" fontSize="lg">
              #{ticket.id} - {ticket.subject}
            </Text>
            <Badge colorScheme={getStatusColor(ticket.status)} size="sm">
              {ticket.status}
            </Badge>
          </Flex>
          
          <Text fontSize="sm" color="gray.500" mb={2}>
            {t("Latest Message")}: {ticket.description || t("No messages")}
          </Text>
          
          <Flex justify="flex-end" mt={3}>
            <Button
              size="sm"
              colorScheme="cyan"
              onClick={() => handleViewTicket(ticket)}
              leftIcon={<MdVisibility />}
            >
              {t("View")}
            </Button>
          </Flex>
        </Card>
      ))}
    </SimpleGrid>
  );

  // Desktop table view
  const renderDesktopView = () => (
    <Box overflowX="auto" maxHeight="600px" overflowY="auto">
      <Table variant="simple" size="md">
        <Thead bg="rgb(69, 50, 240)" position="sticky" top={0} zIndex={1}>
          <Tr>
            <Th color="white">{t("ID")}</Th>
            <Th color="white">{t("Subject")}</Th>
            <Th color="white">{t("Status")}</Th>
            <Th color="white">{t("Description")}</Th>
            <Th color="white">{t("Actions")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredTickets.map((ticket) => (
            <Tr key={ticket.id} _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold">#{ticket.id}</Td>
              <Td maxW="200px" isTruncated>
                {ticket.subject}
              </Td>
              <Td>
                <Badge colorScheme={getStatusColor(ticket.status)} size="sm">
                  {ticket.status}
                </Badge>
              </Td>
              <Td maxW="300px" isTruncated fontSize="sm">
                {ticket.description  || t("No messages")}
              </Td>
              <Td>
                <Tooltip label={t("View Ticket")}>
                  <IconButton
                    size="sm"
                    colorScheme="cyan"
                    icon={<MdVisibility />}
                    onClick={() => handleViewTicket(ticket)}
                  />
                </Tooltip>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ base: "scroll", lg: "hidden" }}>
      {/* Header */}
      <Flex
        px={{ base: "15px", md: "35px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
      >
        <Text
          color="black"
          fontSize={{ base: "18px", md: "22px" }}
          fontWeight="700"
          lineHeight="100%"
        >
          {t("Support Tickets")}
        </Text>
        
        <Flex gap={2} align="center">
          <Button
            leftIcon={<MdRefresh />}
            size="sm"
            colorScheme="gray"
            onClick={() => fetchTickets(currentPage)}
            isLoading={loading}
          >
            {t("Refresh")}
          </Button>
          
          <Button
            colorScheme="cyan"
            color="white"
            size="sm"
            borderRadius="full"
            mt={2}
            onClick={onCreateOpen}
            isDisabled={!permissions.canInsert}
          >
            <MdAdd />
          </Button>
        </Flex>
      </Flex>

      {/* Filters */}
      <Flex
        px={{ base: "15px", md: "35px" }}
        justify="space-between"
        mb="20px"
        align="center"
        flexWrap="wrap"
        gap={4}
      >
        <Input
          placeholder={t("Search tickets...")}
          value={searchQuery}
          onChange={handleSearch}
          size="sm"
          maxW="300px"
        />
        
        <Select
          placeholder={t("All Status")}
          value={statusFilter}
          onChange={(e) => handleStatusFilter(e.target.value)}
          size="sm"
          maxW="200px"
        >
          <option value="pending">{t("Pending")}</option>
          <option value="closed">{t("Closed")}</option>
        </Select>
      </Flex>

      {/* Content */}
      {loading ? (
        <Box p={8} textAlign="center">
          <Text>{t("Loading tickets...")}</Text>
        </Box>
      ) : filteredTickets.length === 0 ? (
        <Box p={8} textAlign="center">
          <Text color="gray.500">{t("No tickets found")}</Text>
        </Box>
      ) : (
        <>
          {isMobile ? renderMobileView() : renderDesktopView()}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Flex 
              justify="space-between" 
              align="center" 
              px={{ base: "15px", md: "35px" }} 
              py="20px"
              borderTop="1px solid"
              borderColor="gray.200"
            >
              <Text fontSize="sm" color="gray.600">
                {t("Showing")} {((currentPage - 1) * limit) + 1} {t("to")} {Math.min(currentPage * limit, totalTickets)} {t("of")} {totalTickets} {t("tickets")}
              </Text>
              
              <Flex gap={2} align="center">
                <Button
                  size="sm"
                  onClick={handlePreviousPage}
                  isDisabled={currentPage === 1}
                  colorScheme="gray"
                >
                  {t("Previous")}
                </Button>
                
                {/* Page Numbers */}
                <Flex gap={1}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={currentPage === pageNum ? "solid" : "outline"}
                        colorScheme={currentPage === pageNum ? "cyan" : "gray"}
                        onClick={() => handlePageChange(pageNum)}
                        minW="40px"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </Flex>
                
                <Button
                  size="sm"
                  onClick={handleNextPage}
                  isDisabled={currentPage === totalPages}
                  colorScheme="gray"
                >
                  {t("Next")}
                </Button>
              </Flex>
            </Flex>
          )}
        </>
      )}

      {/* Create Ticket Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Create New Ticket")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateTicket onTicketCreated={handleTicketCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Ticket Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("Ticket")} #{selectedTicket?.id} - {selectedTicket?.subject}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTicket && (
              <TicketDetail
                ticket={selectedTicket}
                onTicketUpdated={handleTicketUpdated}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default TicketsList;
